/**
 * {@link TeamManagement} — create teams, assign/remove participants to/from teams.
 *
 * Requirements: 3.5, 3.6, 3.8
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

export interface TeamItem {
  publicId: string;
  name: string;
  eventScopedTeamId?: string;
  members?: ParticipantOption[];
  participantCount?: number;
}

export interface ParticipantOption {
  publicId: string;
  name: string;
  email?: string;
  teamId?: string | null;
}

export interface TeamManagementProps {
  eventId: string;
  apiClient: ApiClient;
}

const sectionStyle: CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '1rem',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  borderRadius: 'var(--radius-button, 8px)',
  border: '1px solid var(--color-border, #E5E7EB)',
};

const selectStyle: CSSProperties = {
  padding: '0.5rem',
  borderRadius: 'var(--radius-button, 8px)',
  border: '1px solid var(--color-border, #E5E7EB)',
};

export function TeamManagement({
  eventId,
  apiClient,
}: TeamManagementProps): JSX.Element {
  const [teams, setTeams] = useState<TeamItem[]>([]);
  const [participants, setParticipants] = useState<ParticipantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamName, setTeamName] = useState('');
  const [creating, setCreating] = useState(false);

  // Assignment state
  const [assignParticipantId, setAssignParticipantId] = useState('');
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [teamsData, participantsData] = await Promise.all([
        apiClient.request<{
          teams: { id: string; name: string; eventScopedTeamId: string; members: { id: string; name: string; contactInfo: string; teamId: string | null }[] }[];
        }>('GET', `/events/${encodeURIComponent(eventId)}/teams`),
        apiClient.request<{
          participants: { id: string; name: string; contactInfo: string; teamId: string | null }[];
        }>('GET', `/events/${encodeURIComponent(eventId)}/participants`),
      ]);
      setTeams(
        teamsData.teams.map((t) => ({
          publicId: t.id,
          name: t.name,
          eventScopedTeamId: t.eventScopedTeamId,
          members: t.members.map((m) => ({ publicId: m.id, name: m.name, email: m.contactInfo, teamId: m.teamId })),
        })),
      );
      setParticipants(
        participantsData.participants.map((p) => ({
          publicId: p.id,
          name: p.name,
          email: p.contactInfo,
          teamId: p.teamId,
        })),
      );
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load teams.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, eventId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function handleCreateTeam(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const name = teamName.trim();
    if (!name) return;

    setCreating(true);
    setError(null);
    try {
      await apiClient.request<unknown>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/teams`,
        { body: { name } },
      );
      setTeamName('');
      await fetchData();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to create team.');
    } finally {
      setCreating(false);
    }
  }

  async function handleAssign(): Promise<void> {
    if (!assignParticipantId || !assignTeamId) return;

    setAssigning(true);
    setError(null);
    try {
      await apiClient.request<void>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/teams/${encodeURIComponent(assignTeamId)}/participants`,
        { body: { participantId: assignParticipantId } },
      );
      setAssignParticipantId('');
      setAssignTeamId('');
      await fetchData();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to assign participant to team.');
    } finally {
      setAssigning(false);
    }
  }

  async function handleRemoveFromTeam(teamId: string, participantId: string): Promise<void> {
    setError(null);
    try {
      await apiClient.request<void>(
        'DELETE',
        `/events/${encodeURIComponent(eventId)}/teams/${encodeURIComponent(teamId)}/participants/${encodeURIComponent(participantId)}`,
      );
      await fetchData();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to remove participant from team.');
    }
  }

  // Group participants by team for display
  const participantsByTeam = new Map<string, ParticipantOption[]>();
  for (const p of participants) {
    if (p.teamId) {
      const existing = participantsByTeam.get(p.teamId) ?? [];
      existing.push(p);
      participantsByTeam.set(p.teamId, existing);
    }
  }

  return (
    <section style={sectionStyle} aria-labelledby="team-mgmt-heading">
      <h3 id="team-mgmt-heading" style={headingStyle}>
        Teams
      </h3>

      {error && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {/* Create team form */}
      <form
        onSubmit={(e) => { void handleCreateTeam(e); }}
        style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
        noValidate
      >
        <label htmlFor="team-name-input" style={{ display: 'none' }}>
          Team name
        </label>
        <input
          id="team-name-input"
          type="text"
          placeholder="New team name"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          style={inputStyle}
          aria-label="Team name"
        />
        <Button type="submit" disabled={creating || !teamName.trim()}>
          {creating ? <Spinner size="1rem" label="Creating…" /> : 'Create Team'}
        </Button>
      </form>

      {/* Assign participant to team */}
      {teams.length > 0 && participants.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <label htmlFor="assign-participant" style={{ display: 'none' }}>
            Select participant
          </label>
          <select
            id="assign-participant"
            value={assignParticipantId}
            onChange={(e) => setAssignParticipantId(e.target.value)}
            style={selectStyle}
            aria-label="Select participant to assign"
          >
            <option value="">Select participant…</option>
            {participants.map((p) => (
              <option key={p.publicId} value={p.publicId}>
                {p.name || p.email || p.publicId}
              </option>
            ))}
          </select>

          <label htmlFor="assign-team" style={{ display: 'none' }}>
            Select team
          </label>
          <select
            id="assign-team"
            value={assignTeamId}
            onChange={(e) => setAssignTeamId(e.target.value)}
            style={selectStyle}
            aria-label="Select team to assign to"
          >
            <option value="">Select team…</option>
            {teams.map((t) => (
              <option key={t.publicId} value={t.publicId}>
                {t.name}
              </option>
            ))}
          </select>

          <Button
            onClick={() => { void handleAssign(); }}
            disabled={assigning || !assignParticipantId || !assignTeamId}
          >
            {assigning ? <Spinner size="1rem" label="Assigning…" /> : 'Assign'}
          </Button>
        </div>
      )}

      {/* Team list with members */}
      {loading ? (
        <Spinner label="Loading teams…" />
      ) : teams.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No teams yet.</p>
      ) : (
        <ul style={listStyle} aria-label="Team list">
          {teams.map((team) => {
            const members = participantsByTeam.get(team.publicId) ?? [];
            return (
              <li key={team.publicId} style={{ ...listItemStyle, flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500 }}>{team.name}</span>
                  <span style={{ color: 'var(--color-text-secondary, #6B7280)', fontSize: 'var(--font-size-body-sm, 0.875rem)' }}>
                    {members.length} member{members.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {members.length > 0 && (
                  <ul style={{ ...listStyle, paddingLeft: '1rem', marginTop: '0.25rem' }}>
                    {members.map((m) => (
                      <li key={m.publicId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                        <span style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)' }}>{m.name || m.email || m.publicId}</span>
                        <Button
                          variant="secondary"
                          onClick={() => { void handleRemoveFromTeam(team.publicId, m.publicId); }}
                          aria-label={`Remove ${m.name || 'participant'} from ${team.name}`}
                          style={{ fontSize: 'var(--font-size-body-sm, 0.875rem)', padding: '0.2rem 0.4rem' }}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
